import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import { Dropdown, SORT_FIELD } from '@app/components';
import { PropsWithClassName, SORT_DIRECTION, SortOption } from '@app/constants';

import './SortByDropdown.scss';

export interface SortByDropdownProps {
  options: SortOption[];
  sortField?: string;
  sortDirection?: string;
  styleType?: 'standard' | 'primary';
  hideLabel?: boolean;
  toggleIcon?: IconDefinition;
  toggleIconOnly?: boolean;
  onSelect: (searchOption: SortOption) => void;
}

export const SortByDropdown: FC<PropsWithClassName<SortByDropdownProps>> = ({
  options,
  sortField = SORT_FIELD.relevance,
  sortDirection = SORT_DIRECTION.desc,
  styleType = 'primary',
  hideLabel,
  toggleIcon,
  toggleIconOnly,
  className,
  onSelect,
}) => {
  const { $t } = useIntl();
  const formattedOptions = useMemo(
    () =>
      options.map(option => ({
        ...option,
        label: $t(option.label, { order: option.sortDirection }),
      })),
    [$t, options]
  );
  const selectedOption =
    formattedOptions.find(
      option =>
        option.sortField === sortField && option.sortDirection === sortDirection
    ) || formattedOptions[0];

  const handleSelectedItemChange = useCallback(
    ({ selectedItem }) => {
      onSelect(selectedItem);
    },
    [onSelect]
  );

  return (
    <Dropdown
      id="sortByDropdown"
      items={formattedOptions}
      className={className ?? 'sortByDropdownPrimary'}
      label={$t({
        id: 'sortByDropdown.label',
        defaultMessage: 'Sort by:',
      })}
      toggleIcon={toggleIcon}
      toggleIconOnly={toggleIconOnly}
      selectedItem={selectedOption}
      hideLabel={hideLabel}
      styleType={styleType}
      onSelectedItemChange={handleSelectedItemChange}
    />
  );
};
